import Swal from 'sweetalert2'
import { isNativeApp, nativePlatform, nativePlugin } from '@/utils/nativeApp'
import { registerMobileDevice, unregisterMobileDevice } from '@/services/wootdashMobileDevices'
import { WOOTDASH_BASE } from '@/api/wootdash'

// Notificações no app do celular (plano-app-mobile.md, fase 3). Só age dentro do app (ionic/); no navegador o painel
// continua com o Web Push. O backend manda pelo Firebase os mesmos avisos do navegador, com o id da conversa — tocar na
// notificação abre a conversa, inclusive com o app fechado (o plugin guarda o toque até este listener existir).

/** Canal das notificações comuns no Android. A atribuição tem canal próprio, criado pelo app nativo com toque de
 * telefone ("Chamada: conversa atribuída a mim", CallAlert.java — fase 4). */
export const MOBILE_CHANNELS = [
  { id: 'mensagens', name: 'Mensagens e avisos', description: 'Mensagem nova nas suas conversas e demais avisos do WootDash', importance: 4, vibration: true },
]

/** Já explicou neste celular o que liberar pro alerta de atribuição (não pergunta de novo a cada login). */
const CALL_SETUP_KEY = 'wootdash.app.alerta-atribuicao-explicado'

let listenersReady = false
let deviceToken: string | null = null
let authTokenForDevice: string | null = null
let openConversation: ((conversationId: string) => void) | null = null

async function deviceInfo(): Promise<{ device: string | null; appVersion: string | null }> {
  try {
    const [device, app] = await Promise.all([nativePlugin('Device')?.getInfo?.(), nativePlugin('App')?.getInfo?.()])
    const model = [device?.manufacturer, device?.model].filter(Boolean).join(' ')
    return { device: model || null, appVersion: app?.version ?? null }
  } catch {
    return { device: null, appVersion: null }
  }
}

async function register(token: string): Promise<void> {
  deviceToken = token
  if (!authTokenForDevice) return
  const platform = nativePlatform() === 'ios' ? 'ios' : 'android'
  const { problem, replyToken } = await registerMobileDevice(authTokenForDevice, { token, platform, ...(await deviceInfo()) })
  if (problem) console.warn('[MobilePush] cadastro do celular:', problem)
  // Resposta rápida (fase 5): o app nativo guarda o token e o endereço da Api pra responder pela notificação sem abrir o painel.
  if (replyToken) {
    const apiBase = new URL(WOOTDASH_BASE, window.location.origin).href
    await nativePlugin('WootDashCall')?.saveReplyToken?.({ token: replyToken, apiBase })?.catch?.(() => undefined)
  }
}

async function attachListeners(push: NonNullable<ReturnType<typeof nativePlugin>>): Promise<void> {
  if (listenersReady) return
  listenersReady = true
  await push.addListener?.('registration', (data: { value?: string }) => { if (data?.value) void register(data.value) })
  await push.addListener?.('registrationError', (err: unknown) => console.warn('[MobilePush] o Firebase recusou o registro:', err))
  // Toque na notificação (app aberto, em segundo plano ou fechado): abre a conversa.
  await push.addListener?.('pushNotificationActionPerformed', (action: { notification?: { data?: Record<string, string> } }) => {
    const id = action?.notification?.data?.conversationId
    if (!id) return
    void nativePlugin('WootDashCall')?.cancel?.({ conversationId: id }) // abriu a conversa: para de tocar
    openConversation?.(id)
  })
}

/**
 * Liga as notificações do app pro agente logado: canais, permissão (o Android 13+ pergunta na primeira vez), token do
 * Firebase e cadastro do celular na Api. Chamar depois do login; trocar de agente no mesmo celular cadastra de novo.
 * Devolve se ficou ligado (falso fora do app ou sem permissão).
 */
export async function startMobilePush(authToken: string, onOpenConversation: (conversationId: string) => void): Promise<boolean> {
  if (!isNativeApp()) return false
  const push = nativePlugin('PushNotifications')
  if (!push) return false

  authTokenForDevice = authToken
  openConversation = onOpenConversation
  await attachListeners(push)

  if (nativePlatform() === 'android') {
    for (const channel of MOBILE_CHANNELS) {
      try { await push.createChannel?.(channel) } catch { /* canal já existe */ }
    }
  }

  let permission = (await push.checkPermissions?.())?.receive
  if (permission === 'prompt' || permission === 'prompt-with-rationale') permission = (await push.requestPermissions?.())?.receive
  if (permission !== 'granted') return false

  // Já tinha token (outro agente logou neste celular): só cadastra pra quem entrou agora.
  if (deviceToken) await register(deviceToken)
  else await push.register?.()
  void explainCallAlertSetup()
  return true
}

/**
 * Alerta de atribuição tipo "ligação" (fase 4): pra tocar com o celular bloqueado, o Android 14+ pede que o agente libere
 * a tela cheia, e Samsung/Xiaomi seguram a notificação se o app estiver na economia de bateria. Explica uma vez por
 * celular e leva direto pra tela do Android onde se libera.
 */
export async function explainCallAlertSetup(force = false): Promise<void> {
  const call = nativePlugin('WootDashCall')
  if (!call?.status) return
  try { if (!force && localStorage.getItem(CALL_SETUP_KEY)) return } catch { /* sem storage: pergunta */ }
  const status = await call.status().catch(() => null) as { fullScreen?: boolean; batteryUnrestricted?: boolean } | null
  if (!status) return
  try { localStorage.setItem(CALL_SETUP_KEY, '1') } catch { /* modo privado */ }

  if (status.fullScreen === false) {
    const answer = await Swal.fire({
      icon: 'info',
      title: 'Alerta de conversa atribuída',
      html: 'Quando uma conversa for atribuída a você, o celular toca como uma ligação. Pra a tela de chamada aparecer com o celular bloqueado, libere <b>"Permitir notificações em tela cheia"</b> para o WootDash.',
      showCancelButton: true, confirmButtonText: 'Liberar agora', cancelButtonText: 'Depois', confirmButtonColor: '#bc0c24',
    })
    if (answer.isConfirmed) await call.openFullScreenSettings?.()
  }
  if (status.batteryUnrestricted === false) {
    const answer = await Swal.fire({
      icon: 'info',
      title: 'Não deixe o celular segurar os avisos',
      html: 'Alguns celulares (Samsung, Xiaomi…) seguram as notificações de apps em economia de bateria. Toque em <b>Permitir</b> na próxima tela pra o WootDash avisar na hora.',
      showCancelButton: true, confirmButtonText: 'Continuar', cancelButtonText: 'Depois', confirmButtonColor: '#bc0c24',
    })
    if (answer.isConfirmed) await call.openBatterySettings?.()
  }
}

/** Logout no app: tira o cadastro deste celular do agente que saiu (o próximo login cadastra de novo) e apaga o token da
 * resposta rápida guardado no aparelho. */
export async function stopMobilePush(authToken: string): Promise<void> {
  if (!isNativeApp()) return
  await nativePlugin('WootDashCall')?.clearReplyToken?.()?.catch?.(() => undefined)
  if (!deviceToken) return
  authTokenForDevice = null
  await unregisterMobileDevice(authToken, deviceToken)
}

/** Só para testes: volta ao estado inicial. */
export function resetMobilePushForTests(): void {
  listenersReady = false
  deviceToken = null
  authTokenForDevice = null
  openConversation = null
}
